import type { FormattingRule } from '@ardoq/data-model';
import {
  FormattingRuleLabel,
  type FormattingContextState,
} from '@ardoq/perspectives';
import type { ReactNode } from 'react';
import { GhostButton, DragButton } from '@ardoq/button';
import { ChevronDownIcon, ChevronUpIcon, DeleteIcon } from '@ardoq/icons';
import { APIEntityType } from '@ardoq/api-types';
import { HeaderRowWrapper } from '../../../atoms';
import { WithPopover } from '@ardoq/popovers';
import { isRuleVersionSupported } from '@ardoq/perspectives';
import { type I18nLocale, t } from '@ardoq/i18n';

type FormattingRuleHeaderProperties = {
  rule: FormattingRule;
  context: FormattingContextState;
  popoverContent?: () => ReactNode;
  label?: string;
  i18nLocale?: I18nLocale;
};
const FormattingRuleHeader = ({
  rule,
  context: { removeRule, updateRule },
  label,
  popoverContent,
  i18nLocale = {},
}: FormattingRuleHeaderProperties) => {
  const isRuleUnsupported = !isRuleVersionSupported(true, rule.version);

  return (
    <HeaderRowWrapper>
      <DragButton draggable={!!rule.__id} i18nLocale={i18nLocale} />
      <WithPopover content={rule.isCollapsed ? popoverContent : undefined}>
        <FormattingRuleLabel
          ruleType={rule.type}
          label={label}
          isSidebar={true}
          isDisabled={isRuleUnsupported}
        />
      </WithPopover>
      <GhostButton
        aria-label={t(
          i18nLocale,
          'perspectivesSidebar:formattingRules.removeFormattingRuleAriaLabel',
          'Remove formatting rule'
        )}
        dataTestId={
          rule.type === APIEntityType.TAG
            ? 'remove-tag-formatting-rule-button'
            : 'remove-formatting-rule-button'
        }
        onClick={() => removeRule(rule.__id)}
      >
        <DeleteIcon />
      </GhostButton>
      <GhostButton
        aria-label={
          rule?.isCollapsed
            ? t(
                i18nLocale,
                'perspectivesSidebar:formattingRules.expandFormattingRule',
                'Expand formatting rule'
              )
            : t(
                i18nLocale,
                'perspectivesSidebar:formattingRules.collapseFormattingRule',
                'Collapse formatting rule'
              )
        }
        onClick={() =>
          updateRule(rule.__id, {
            isCollapsed: !rule?.isCollapsed,
          })
        }
      >
        {rule?.isCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
      </GhostButton>
    </HeaderRowWrapper>
  );
};

export { FormattingRuleHeader };
